import { VariantPrice } from "../../../../types/types";
import styles from "./VariantAttributes.module.scss";
import { useEffect, useState } from "react";
import {
  SELECTED_SERVICES_UPDATED,
  ADD_TO_BASKET_ESI_LENGTH_UPDATED,
} from "../../../../constants/constants";

type Props = {
  hasAnyStock: boolean;
  priceDisplay: VariantPrice | string;
  dimensions: string | null | undefined;
  size: string | null | undefined;
  colour: string | null | undefined;
  isPerMetre: boolean;
  perMetreMinimumLength: number | null | undefined;
};

//TODO: do we need variantSize, variantColour and variantDimensions as String props instead of selectedVariant
export function VariantAttributes({
  hasAnyStock,
  priceDisplay,
  dimensions,
  size,
  colour,
  isPerMetre,
  perMetreMinimumLength,
}: Props) {
  if (!hasAnyStock) {
    return null;
  }

  return (
    <VariantAttributesUI
      price={priceDisplay}
      colour={colour}
      size={size}
      dimensions={dimensions}
      isPerMetre={isPerMetre}
      perMetreMinimumLength={perMetreMinimumLength}
    />
  );
}

type VariantAttributesProps = {
  /**
   * An object or string representing the price of the product.
   */
  price: VariantPrice | string;
  /**
   * A string representing the colour of the product. It can also be null or undefined.
   */
  colour: string | null | undefined;
  /**
   * A string representing the size of the product. It can also be null or undefined.
   */
  size: string | null | undefined;
  /**
   * A string representing the dimensions of the product. It can also be null or undefined.
   */
  dimensions: string | null | undefined;
  /**
   * A boolean representing if the product is sold by the metre
   */
  isPerMetre: boolean;
  /**
   * A number representing the minimum length of a product sold by the metre
   */
  perMetreMinimumLength: number | null | undefined;
};

const VariantAttributesUI = ({
  price,
  colour,
  size,
  dimensions,
  isPerMetre,
  perMetreMinimumLength,
}: VariantAttributesProps) => {
  const [selectedServicesPrice, setSelectedServicesPrice] = useState();
  const [selectedServicesCount, setSelectedServicesCount] = useState();
  const [lengthSelected, setLengthSelected] = useState(perMetreMinimumLength);

  useEffect(() => {
    const handleSelectedServicesUpdated = (event: Event) => {
      const { selectedServicesPrice, selectedServicesCount } = (
        event as CustomEvent
      ).detail;
      setSelectedServicesPrice(selectedServicesPrice);
      setSelectedServicesCount(selectedServicesCount);
    };

    const handleAddToBasketPerMetreLengthUpdated = (event: Event) => {
      const { quantity } = (event as CustomEvent).detail;
      setLengthSelected(quantity);
    };

    window.addEventListener(
      SELECTED_SERVICES_UPDATED,
      handleSelectedServicesUpdated,
    );
    if (isPerMetre) {
      window.addEventListener(
        ADD_TO_BASKET_ESI_LENGTH_UPDATED,
        handleAddToBasketPerMetreLengthUpdated,
      );
    }
    return () => {
      window.removeEventListener(
        SELECTED_SERVICES_UPDATED,
        handleSelectedServicesUpdated,
      );
      if (isPerMetre) {
        window.removeEventListener(
          ADD_TO_BASKET_ESI_LENGTH_UPDATED,
          handleAddToBasketPerMetreLengthUpdated,
        );
      }
    };
  }, []);

  if (!price && !colour && !size) return null;

  const getFormattedPrice = () => {
    if (typeof price === "string") return price;
    let displayPrice = price?.display;
    if (price?.value && selectedServicesPrice !== undefined) {
      displayPrice = `£${
        (Number(price.value) + Number(selectedServicesPrice)).toFixed(2) ?? 0
      }${selectedServicesCount ? " (inc. additional services)" : ""}`;
    } else if (price?.value && lengthSelected) {
      displayPrice = `£${(Number(price.value) * lengthSelected).toFixed(2)}`;
    }
    return displayPrice;
  };

  return (
    <dl
      data-testid="basket:product:attributes:list"
      className={styles.attributeList}
    >
      {colour && (
        <>
          <dt className={styles.attributeName}>Colour:</dt>
          <dd data-testid="colour:attribute" className={styles.attributeValue}>
            {colour}
          </dd>
        </>
      )}
      {size && (
        <>
          <dt className={styles.attributeName}>Size:</dt>
          <dd data-testid="size:attribute" className={styles.attributeValue}>
            {dimensions ? `${size} ${dimensions}` : size}
          </dd>
        </>
      )}
      {price && (
        <>
          {isPerMetre && (
            <dt className={styles.attributeEmptyValue}>Total fabric price</dt>
          )}
          <dt className={styles.visuallyHidden}>Price</dt>
          <dd
            data-testid="product:basket:price"
            className={styles.attributeEmptyValue}
          >
            {getFormattedPrice()}
          </dd>
        </>
      )}
    </dl>
  );
};
